import React, { useEffect, useRef, useId } from 'react';
import {
  parseReplyRemarkToMessages,
  isMessageFromCurrentUser,
  getCurrentUserReplySignature,
  getComposerMaxLength,
  REPLY_REMARK_MAX_LENGTH,
} from '../../utils/actionItemReplyThread';
import './ActionItemReplyThread.css';

/**
 * Chat-style read-only thread + composer for CRF issue Description of Action (vReplyRemark).
 */
export default function ActionItemReplyThread({
  persistedRemark,
  historyEntries,
  draft,
  onDraftChange,
  readOnly,
  user,
  fieldError,
  showRequiredStar,
  threadAriaLabel = 'Description of Action message history',
  composerPlaceholder = 'Enter Description of Action',
}) {
  const messages = Array.isArray(historyEntries) && historyEntries.length > 0
    ? historyEntries
    : parseReplyRemarkToMessages(persistedRemark);
  const threadRef = useRef(null);
  const composerId = useId();
  const signature = getCurrentUserReplySignature(user);
  const maxLen = getComposerMaxLength(persistedRemark || '');

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [persistedRemark, messages.length]);

  return (
    <div className="action-item-reply-thread">
      <div
        ref={threadRef}
        className="action-item-reply-thread__scroll"
        role="log"
        aria-label={threadAriaLabel}
        tabIndex={0}
      >
        {messages.length === 0 ? (
          <div className="action-item-reply-thread__empty text-muted">No messages yet.</div>
        ) : (
          messages.map((msg, idx) => {
            const isAuditEntry = Object.prototype.hasOwnProperty.call(msg || {}, 'metaText');
            const mine = isAuditEntry
              ? Boolean(msg.isMine)
              : isMessageFromCurrentUser(msg, user);
            const bubbleClass = mine
              ? 'action-item-reply-thread__bubble action-item-reply-thread__bubble--mine'
              : 'action-item-reply-thread__bubble action-item-reply-thread__bubble--other';
            const meta = isAuditEntry
              ? (msg.metaText || '')
              : (msg.isLegacy
                ? ''
                : `${msg.displayName} (${msg.login})(${msg.profile}) ${msg.datePart} ${msg.timePart}`);
            return (
              <div
                key={msg.key || `entry-${idx}`}
                className={`action-item-reply-thread__row${mine ? ' action-item-reply-thread__row--mine' : ''}`}
              >
                <div className={bubbleClass}>
                  {meta ? <div className="action-item-reply-thread__meta">{meta}</div> : null}
                  <div className="action-item-reply-thread__body">{msg.body || ''}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!readOnly ? (
        <div className="action-item-reply-thread__composer-wrap">
         
          <div className="action-item-reply-thread__composer-bar">
            <label className="visually-hidden" htmlFor={composerId}>
              New reply text
            </label>
            <textarea
              id={composerId}
              className={`action-item-reply-thread__input${fieldError ? ' action-item-reply-thread__input--invalid' : ''}`}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              placeholder={composerPlaceholder}
              rows={2}
              maxLength={maxLen > 0 ? maxLen : REPLY_REMARK_MAX_LENGTH}
              aria-invalid={Boolean(fieldError)}
              aria-describedby={fieldError ? `${composerId}-err` : `${composerId}-budget`}
            />
            <span className="visually-hidden" id={`${composerId}-budget`}>
              Up to approximately {maxLen} characters for this reply given existing history.
            </span>
          </div>
        </div>
      ) : null}

      {fieldError ? (
        <div className="action-item-reply-thread__error" id={`${composerId}-err`} role="alert">
          {fieldError}
        </div>
      ) : null}
      
    </div>
  );
}
